/**
 * MOCK FIXTURE DATA - Dashboard app
 * ---------------------------------
 * This file is local, clearly-labeled mock data standing in for the real
 * backend while it is built out by the other services in this monorepo.
 * Every shape below matches the shared data contract exactly (see
 * `@hp/types`) so swapping the functions in `src/api/*` over to real
 * `@hp/api-client` calls later requires no shape changes anywhere in this
 * app - only the function bodies change.
 *
 * The fixtures model the same real company as the other apps in this
 * monorepo ("Hindustan Power Ltd") so the dashboard reads as a
 * management-tier view onto the same underlying business, not a different
 * universe:
 *  - Five of the company's own Findings have reached a favorable, citable
 *    outcome (one of them - the capacity-charge win - is itself now under a
 *    discom special leave petition, so it is both "favorable" and "pending
 *    appeal" at once, which is realistic and deliberately modeled).
 *  - Six of the company's own Findings went against it (disallowed/partial)
 *    and drive the exposure view.
 *  - A handful of CompanyDisallowance records represent historical amounts
 *    withheld against agreement clauses, some now unlocked by a favorable
 *    Finding (and so feeding the claim-candidate pipeline below), some still
 *    open and unmatched, and two finally written off (status "dismissed").
 *  - Twenty-two ClaimCandidate records populate every pipeline stage, with
 *    `created_at` timestamps spread across the last three months so the
 *    periodic digest (see `src/api/digest.ts`) has real month-over-month
 *    history to show.
 */
import type { CilChangeEvent, CilDecision, CilExplanation, CilSummary } from "@hp/types";
import type {
  AgreementClause,
  AuthorityStatus,
  AuthorityStatusValue,
  ClaimCandidate,
  ClaimCandidateStatus,
  CompanyAgreement,
  CompanyDisallowance,
  Finding,
  FindingOutcome,
  Forum,
} from "@hp/types";

// ---------------------------------------------------------------------------
// Deterministic fixture ids
// ---------------------------------------------------------------------------
// Real UUIDs, generated deterministically from a short readable seed so the
// same seed always maps to the same id and cross-references below never
// drift out of sync as the file is edited.

function mockId(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x9e3779b9;
  let h3 = 0x85ebca6b;
  let h4 = 0xc2b2ae35;
  for (let i = 0; i < seed.length; i++) {
    const c = seed.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619);
    h2 = Math.imul(h2 ^ c, 2654435761);
    h3 = Math.imul(h3 ^ c, 2246822519);
    h4 = Math.imul(h4 ^ c, 3266489917);
  }
  const hex = (x: number) => (x >>> 0).toString(16).padStart(8, "0");
  const a = hex(h1);
  const b = hex(h2);
  const c = hex(h3);
  const d = hex(h4);
  return `${a}-${b.slice(0, 4)}-4${b.slice(4, 7)}-8${c.slice(0, 3)}-${c.slice(3, 8)}${d.slice(0, 7)}`;
}

const fid = (key: string) => mockId(`finding:${key}`);
const aid = (key: string) => mockId(`agreement:${key}`);
const clid = (key: string) => mockId(`clause:${key}`);
const did = (key: string) => mockId(`disallowance:${key}`);
const ccid = (key: string) => mockId(`claim-candidate:${key}`);

// ---------------------------------------------------------------------------
// The golden case (real) - Tata Power v. MERC (2013) -> JPVL v. MPERC ->
// MB Power's actual FY2015-16 to FY2024-25 RoE gross-up recovery of
// ₹322.8 Cr, per "HP Claims Radar - dashboard.html" (the original POC
// mockup this reproduces with a live, running system instead of a static
// page). Every name, date, case number, and amount below is real - the
// one part of this fixture universe that is not fictional.
// ---------------------------------------------------------------------------

const gid = (key: string) => mockId(`entitlement-gap:${key}`);

const GOLDEN = {
  tataPower: fid("golden:tata-power-2013"),
  jpvlMpercDisallowed: fid("golden:jpvl-mperc-disallowed"),
  aptel2024: fid("golden:aptel-2024-first-appeal"),
  aptel2025: fid("golden:aptel-2025-reversal"),
  agreement: aid("golden:mb-power-mp-ppa-30pct"),
  clauseTariff: clid("golden:article-10-1-1"),
  clauseChangeInLaw: clid("golden:article-12-1-1"),
  clauseCarryingCost: clid("golden:article-10-4-2"),
  gapFy201516: gid("golden:fy2015-16"),
  claimCandidate: ccid("golden:mb-power-claim-fy2015-16"),
} as const;

// ---------------------------------------------------------------------------
// Company universe (fictional)
// ---------------------------------------------------------------------------

export const COMPANY_NAME = "Hindustan Power Ltd";
const DISCOM_1 = "Rajpath Vidyut Vitran Nigam Ltd";
const DISCOM_2 = "Deccan Distribution Ltd";
const DISCOM_3 = "Central Power Distribution Corp";
const MINISTRY = "Ministry of Power, Government of India";
const COAL_CO = "Vindhya Coal Ltd";

// ---------------------------------------------------------------------------
// Findings
// ---------------------------------------------------------------------------

function span(page: number, paragraphRef: string) {
  return [
    { page_start: page, page_end: page + 1, char_start: 120, char_end: 1450, paragraph_ref: paragraphRef },
  ];
}

function confidenceSet(overall: number): Record<string, number> {
  return {
    issue_tag: Math.min(0.99, overall + 0.05),
    outcome: Math.min(0.99, overall + 0.03),
    quantum_amount: Math.max(0.3, overall - 0.08),
    ratio_summary: overall,
  };
}

const F = {
  CIL: fid("F_CIL"),
  CIL_EXT: fid("F_CIL_EXT"),
  TRUEUP: fid("F_TRUEUP"),
  FSA: fid("F_FSA"),
  LPS: fid("F_LPS"),
  CAP: fid("F_CAP"),
  ADV_WC: fid("F_ADV_WC"),
  ADV_DGC: fid("F_ADV_DGC"),
  ADV_OM: fid("F_ADV_OM"),
  ADV_IDP: fid("F_ADV_IDP"),
  ADV_TL: fid("F_ADV_TL"),
  ADV_CQ: fid("F_ADV_CQ"),
} as const;

interface FindingSeed {
  id: string;
  forum: Forum;
  case_number: string;
  decision_date: string;
  bench: string;
  petitioner: string;
  respondent: string;
  plant_ref: string | null;
  fuel_type: string | null;
  capacity_mw: number | null;
  agreement_type: string | null;
  act_sections: string[];
  regulation_refs: string[];
  rule_refs: string[];
  issue_tag: string;
  outcome: FindingOutcome;
  quantum_amount: number | null;
  quantum_basis: string | null;
  ratio_summary: string;
  precedents_cited: string[];
  page: number;
  para: string;
  confidence: number;
  validated: boolean;
}

const FINDING_SEEDS: FindingSeed[] = [
  // --- Favorable / citable precedent ---
  {
    id: F.CIL,
    forum: "supreme_court",
    case_number: "Civil Appeal No. 4521 of 2021",
    decision_date: "2023-01-18",
    bench: "Justice R. Menon; Justice P. Iyer",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_1,
    plant_ref: "Suryachal TPS Unit 1&2",
    fuel_type: "coal",
    capacity_mw: 1000,
    agreement_type: "cost_plus",
    act_sections: ["Section 63", "Section 86(1)(b)"],
    regulation_refs: ["CERC (Tariff) Regulations 2019, Regulation 12"],
    rule_refs: [],
    issue_tag: "change_in_law_coal_cess",
    outcome: "allowed",
    quantum_amount: 58000000,
    quantum_basis: "Full coal cess pass-through for FY2017-20 affirmed in full, reversing the Commission's single-year cut-off.",
    ratio_summary:
      "Supreme Court affirms that a validly-levied cess of general application qualifies as change in law for the entire period it subsists.",
    precedents_cited: [F.CIL_EXT],
    page: 2,
    para: "¶ 9",
    confidence: 0.95,
    validated: true,
  },
  {
    id: F.TRUEUP,
    forum: "supreme_court",
    case_number: "Civil Appeal No. 6210 of 2020",
    decision_date: "2024-10-09",
    bench: "Justice R. Menon; Justice K. Bose",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_3,
    plant_ref: "Konkan Coastal Unit 2",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "bid_route",
    act_sections: ["Section 64", "Section 111"],
    regulation_refs: ["CERC (Tariff) Regulations 2014, Regulation 8"],
    rule_refs: [],
    issue_tag: "tariff_true_up_denial",
    outcome: "allowed",
    quantum_amount: 94000000,
    quantum_basis: "Tribunal's true-up quantum of INR 9.4 Cr affirmed in full; discom's appeal dismissed.",
    ratio_summary:
      "Limitation for a true-up petition runs from communication of the order, settling the point for all pending matters on the same issue.",
    precedents_cited: [],
    page: 4,
    para: "¶ 12",
    confidence: 0.96,
    validated: true,
  },
  {
    id: F.FSA,
    forum: "central_ministry",
    case_number: "OM No. F.10-18/2024-Power",
    decision_date: "2024-11-02",
    bench: "Joint Secretary (Thermal)",
    petitioner: COMPANY_NAME,
    respondent: MINISTRY,
    plant_ref: "Konkan Coastal Unit 1",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "fuel_supply",
    act_sections: ["Section 79(1)(b)"],
    regulation_refs: [],
    rule_refs: ["Fuel Supply Agreement Rules, Rule 9"],
    issue_tag: "fuel_supply_agreement_shortfall",
    outcome: "allowed",
    quantum_amount: 16800000,
    quantum_basis: "FY2023-24 certified linkage shortfall compensation allowed in full.",
    ratio_summary:
      "Ministry directs the coal supplier to compensate the generator for the certified shortfall at the notified e-auction price differential.",
    precedents_cited: [],
    page: 3,
    para: "¶ 6",
    confidence: 0.85,
    validated: false,
  },
  {
    id: F.LPS,
    forum: "appellate_tribunal",
    case_number: "Appeal No. 118 of 2024",
    decision_date: "2025-02-14",
    bench: "Justice A.K. Sharma (Chairperson); Member (Technical)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_2,
    plant_ref: "Godavari Peaking Station",
    fuel_type: "gas",
    capacity_mw: 350,
    agreement_type: "bid_route",
    act_sections: ["Section 86(1)(f)"],
    regulation_refs: ["State Commission (Tariff) Regulations, Regulation 42"],
    rule_refs: [],
    issue_tag: "late_payment_surcharge",
    outcome: "allowed",
    quantum_amount: 9800000,
    quantum_basis: "Late payment surcharge upheld as compensatory in character; full arrears awarded.",
    ratio_summary:
      "Surcharge for delayed payment accrues automatically once the credit period lapses and is not defeated by the absence of a fresh demand notice.",
    precedents_cited: [],
    page: 6,
    para: "¶ 10",
    confidence: 0.9,
    validated: false,
  },
  {
    id: F.CAP,
    forum: "appellate_tribunal",
    case_number: "Appeal No. 96 of 2023",
    decision_date: "2024-05-20",
    bench: "Justice S. Rao (Chairperson); Member (Technical)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_1,
    plant_ref: "Suryachal TPS Unit 3",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: ["CERC (Tariff) Regulations 2019, Regulation 27"],
    rule_refs: ["Grid Code, Rule 5.2"],
    issue_tag: "capacity_charge_forced_outage",
    outcome: "allowed",
    quantum_amount: 31500000,
    quantum_basis: "Capacity charge for forced-outage hours recomputed and allowed per the revised availability formula.",
    ratio_summary:
      "Generation logs on record support the claimed outage hours; the Commission's disallowance is set aside.",
    precedents_cited: [],
    page: 20,
    para: "¶ 33",
    confidence: 0.78,
    validated: false,
  },
  // --- Cited external precedent (another company's matter) ---
  {
    id: F.CIL_EXT,
    forum: "supreme_court",
    case_number: "Civil Appeal No. 1188 of 2016",
    decision_date: "2017-09-05",
    bench: "Justice R. Menon; Justice T. Gupta",
    petitioner: "Konkan Coastal Energy Ltd",
    respondent: DISCOM_3,
    plant_ref: null,
    fuel_type: null,
    capacity_mw: null,
    agreement_type: null,
    act_sections: ["Section 63"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "change_in_law_coal_cess",
    outcome: "allowed",
    quantum_amount: 41000000,
    quantum_basis: "Coal cess held a change in law event; full pass-through allowed.",
    ratio_summary:
      "A cess of general application levied by Parliament after the cut-off date is a change in law regardless of whether the PPA lists it by name.",
    precedents_cited: [],
    page: 2,
    para: "¶ 6",
    confidence: 0.9,
    validated: false,
  },
  // --- Adverse findings (drive the exposure view) ---
  {
    id: F.ADV_WC,
    forum: "state_commission",
    case_number: "Petition No. 97 of 2023",
    decision_date: "2023-08-01",
    bench: "Chairperson; Member (Finance)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_1,
    plant_ref: "Suryachal TPS Unit 3",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: ["CERC (Tariff) Regulations 2019, Regulation 34"],
    rule_refs: [],
    issue_tag: "working_capital_carrying_cost",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis: "Working capital carrying cost claim rejected under normative O&M norms.",
    ratio_summary:
      "Commission holds normative working capital under Regulation 34 already accounts for the receivables cycle claimed.",
    precedents_cited: [],
    page: 16,
    para: "¶ 27",
    confidence: 0.71,
    validated: false,
  },
  {
    id: F.ADV_DGC,
    forum: "supreme_court",
    case_number: "Civil Appeal No. 3007 of 2021",
    decision_date: "2022-03-29",
    bench: "Justice P. Iyer; Justice K. Bose",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_3,
    plant_ref: "Suryachal TPS Unit 1&2",
    fuel_type: "coal",
    capacity_mw: 1000,
    agreement_type: "cost_plus",
    act_sections: ["Section 86(1)(f)"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "deemed_generation_compensation",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis: "Deemed generation compensation claim rejected; outage held generator-attributable.",
    ratio_summary:
      "Supreme Court affirms the finding of fact that the outage was generator-attributable and declines to disturb the Tribunal's ruling.",
    precedents_cited: [],
    page: 7,
    para: "¶ 14",
    confidence: 0.93,
    validated: false,
  },
  {
    id: F.ADV_OM,
    forum: "appellate_tribunal",
    case_number: "Appeal No. 203 of 2024",
    decision_date: "2025-06-10",
    bench: "Justice A.K. Sharma (Chairperson); Member (Finance)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_3,
    plant_ref: "Konkan Coastal Unit 2",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "bid_route",
    act_sections: ["Section 62"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "o_and_m_escalation_formula",
    outcome: "partial",
    quantum_amount: 12000000,
    quantum_basis:
      "O&M escalation allowed at the WPI-linked formula for FY2023-24 only; CPI-linked component for later years disallowed pending clarification.",
    ratio_summary:
      "Tribunal splits the escalation formula dispute, allowing the WPI-linked component while staying its own order on the CPI-linked component pending a larger bench reference.",
    precedents_cited: [],
    page: 11,
    para: "¶ 19",
    confidence: 0.65,
    validated: false,
  },
  {
    id: F.ADV_IDP,
    forum: "state_commission",
    case_number: "Petition No. 212 of 2024",
    decision_date: "2025-01-22",
    bench: "Chairperson; Member (Law)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_2,
    plant_ref: "Godavari Peaking Station",
    fuel_type: "gas",
    capacity_mw: 350,
    agreement_type: "bid_route",
    act_sections: ["Section 86(1)(f)"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "interest_on_delayed_payment",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis: "Interest on delayed monthly payments rejected; surcharge held to be the sole remedy for delay.",
    ratio_summary:
      "Commission holds the late payment surcharge clause occupies the field and an additional interest claim for the same delay is not maintainable.",
    precedents_cited: [],
    page: 9,
    para: "¶ 15",
    confidence: 0.69,
    validated: false,
  },
  {
    id: F.ADV_TL,
    forum: "central_tribunal",
    case_number: "Petition No. 88/MP/2023",
    decision_date: "2024-08-09",
    bench: "Chairperson; Member (Technical)",
    petitioner: COMPANY_NAME,
    respondent: DISCOM_3,
    plant_ref: "Suryachal TPS Unit 1&2",
    fuel_type: "coal",
    capacity_mw: 1000,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "transmission_loss_sharing",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis:
      "Transmission loss sharing claim for FY2021-22 rejected; loss allocation held to follow the state grid code formula, not the PPA.",
    ratio_summary:
      "Tribunal holds the grid code's loss-sharing formula prevails over the bilateral PPA clause absent a specific carve-out.",
    precedents_cited: [],
    page: 8,
    para: "¶ 13",
    confidence: 0.74,
    validated: false,
  },
  {
    id: F.ADV_CQ,
    forum: "state_commission",
    case_number: "Petition No. 145 of 2025",
    decision_date: "2025-09-05",
    bench: "Chairperson; Member (Technical)",
    petitioner: COMPANY_NAME,
    respondent: COAL_CO,
    plant_ref: "Konkan Coastal Unit 1",
    fuel_type: "coal",
    capacity_mw: 660,
    agreement_type: "fuel_supply",
    act_sections: [],
    regulation_refs: [],
    rule_refs: ["Fuel Supply Agreement Rules, Rule 11"],
    issue_tag: "coal_quality_grade_slippage",
    outcome: "partial",
    quantum_amount: 4100000,
    quantum_basis:
      "Grade slippage compensation allowed for 2 of 4 disputed quarters pending a third-party coal sampling report for the remainder.",
    ratio_summary: "Commission accepts the sampling evidence for two quarters and defers the balance pending an independent report.",
    precedents_cited: [],
    page: 5,
    para: "¶ 8",
    confidence: 0.6,
    validated: false,
  },

  // --- The golden case (real): Tata Power v. MERC -> JPVL v. MPERC -> MB Power ---
  {
    id: GOLDEN.tataPower,
    forum: "appellate_tribunal",
    case_number: "Appeal Nos. 104-106 of 2012",
    decision_date: "2013-11-28",
    bench: "Justice Rakesh Nagar (Chairperson); Rakesh Nath (Technical Member)",
    petitioner: "Tata Power Company Ltd",
    respondent: "Maharashtra Electricity Regulatory Commission",
    plant_ref: null,
    fuel_type: null,
    capacity_mw: null,
    agreement_type: null,
    act_sections: ["Section 61", "Section 62"],
    regulation_refs: [],
    rule_refs: [],
    issue_tag: "roe_tax_gross_up",
    outcome: "allowed",
    quantum_amount: null,
    quantum_basis: null,
    ratio_summary:
      "Return on equity must be grossed up at the tax rate applicable to the generating company, not the tax actually paid by it - a generator should not lose the promised post-tax return merely because losses elsewhere in the corporate group reduce or eliminate the tax actually payable.",
    precedents_cited: [],
    page: 11,
    para: "¶ 34",
    confidence: 0.95,
    validated: true,
  },
  {
    id: GOLDEN.jpvlMpercDisallowed,
    forum: "state_commission",
    case_number: "Petition Nos. 34, 56, 78 and 92 of 2016-2021 (Bina TPS true-up)",
    decision_date: "2021-03-30",
    bench: "Chairperson; Member (Technical); Member (Law)",
    petitioner: "Jaiprakash Power Ventures Ltd",
    respondent: "Madhya Pradesh Electricity Regulatory Commission",
    plant_ref: "Bina Thermal Power Station",
    fuel_type: "coal",
    capacity_mw: 500,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: ["MPERC (Terms and Conditions for Determination of Tariff) Regulations, 2015, Regulation 22"],
    rule_refs: [],
    issue_tag: "roe_tax_gross_up",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis: "Gross-up claimed against the applicable tax rate across four successive true-ups for Bina TPS.",
    ratio_summary:
      "Regulation 22 grosses up return on equity only for tax 'actually paid' by the generating company for the relevant year; since JPVL reported NIL corporate tax at the consolidated entity level, no gross-up is payable regardless of the notional applicable rate.",
    precedents_cited: [],
    page: 6,
    para: "¶ 19",
    confidence: 0.88,
    validated: true,
  },
  {
    id: GOLDEN.aptel2024,
    forum: "appellate_tribunal",
    case_number: "Appeal No. 283 of 2017 (with connected appeals)",
    decision_date: "2024-03-22",
    bench: "Justice Ramesh Ranganathan (Chairperson); Sandesh Kumar Sharma (Technical Member)",
    petitioner: "Jaiprakash Power Ventures Ltd",
    respondent: "Madhya Pradesh Electricity Regulatory Commission",
    plant_ref: "Bina Thermal Power Station",
    fuel_type: "coal",
    capacity_mw: 500,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: ["MPERC (Terms and Conditions for Determination of Tariff) Regulations, 2015, Regulation 22"],
    rule_refs: [],
    issue_tag: "roe_tax_gross_up",
    outcome: "disallowed",
    quantum_amount: null,
    quantum_basis: null,
    ratio_summary:
      "Affirms MPERC's reading of Regulation 22: absent tax actually paid at the standalone or consolidated level for the relevant year, no gross-up accrues. JPVL's Bina TPS claim for FY2015-16 onward remains disallowed.",
    precedents_cited: [],
    page: 9,
    para: "¶ 28",
    confidence: 0.8,
    validated: true,
  },
  {
    id: GOLDEN.aptel2025,
    forum: "appellate_tribunal",
    case_number: "Appeal No. 113 of 2022",
    decision_date: "2025-07-28",
    bench: "Justice Ramesh Ranganathan (Chairperson); Sandesh Kumar Sharma (Technical Member)",
    petitioner: "Jaiprakash Power Ventures Ltd",
    respondent: "Madhya Pradesh Electricity Regulatory Commission",
    plant_ref: "Bina Thermal Power Station",
    fuel_type: "coal",
    capacity_mw: 500,
    agreement_type: "cost_plus",
    act_sections: ["Section 62"],
    regulation_refs: [
      "MPERC (Terms and Conditions for Determination of Tariff) Regulations, 2015, Regulation 22",
      "MPERC (Terms and Conditions for Determination of Tariff) Regulations, 2024, Regulation 33",
    ],
    rule_refs: [],
    issue_tag: "roe_tax_gross_up",
    outcome: "allowed",
    quantum_amount: null,
    quantum_basis: null,
    ratio_summary:
      "Applying the Tata Power ring-fencing principle directly: gross-up on return on equity runs on the tax rate applicable to the generating company, irrespective of tax actually paid at the consolidated entity level. JPVL's FY2019-20 claim for Bina TPS is allowed; the Commission's contrary reading of Regulation 22 in the disallowed true-ups is overtaken.",
    precedents_cited: [GOLDEN.tataPower],
    page: 14,
    para: "¶ 41",
    confidence: 0.93,
    validated: true,
  },
];

export const FINDINGS: Finding[] = FINDING_SEEDS.map((seed) => ({
  id: seed.id,
  doc_id: mockId(`doc:${seed.id}`),
  doc_version: 1,
  forum: seed.forum,
  case_number: seed.case_number,
  decision_date: seed.decision_date,
  bench: seed.bench,
  petitioner: seed.petitioner,
  respondent: seed.respondent,
  plant_ref: seed.plant_ref,
  fuel_type: seed.fuel_type,
  capacity_mw: seed.capacity_mw,
  agreement_type: seed.agreement_type,
  act_sections: seed.act_sections,
  regulation_refs: seed.regulation_refs,
  rule_refs: seed.rule_refs,
  issue_tag: seed.issue_tag,
  outcome: seed.outcome,
  quantum_amount: seed.quantum_amount,
  quantum_basis: seed.quantum_basis,
  ratio_summary: seed.ratio_summary,
  precedents_cited: seed.precedents_cited,
  source_spans: span(seed.page, seed.para),
  field_confidence: confidenceSet(seed.confidence),
  overall_confidence: seed.confidence,
  validated_by: seed.validated ? "priya.nair@suryachalpower.example" : null,
  validated_at: seed.validated ? "2026-08-20T09:00:00Z" : null,
}));

// ---------------------------------------------------------------------------
// Authority statuses - one per Finding, recomputed on a schedule upstream
// ---------------------------------------------------------------------------

function authority(findingId: string, status: AuthorityStatusValue): AuthorityStatus {
  return {
    finding_id: findingId,
    status,
    as_of: "2026-09-08T06:00:00Z",
    computed_at: "2026-09-08T06:15:00Z",
  };
}

export const AUTHORITY_STATUSES: AuthorityStatus[] = [
  authority(F.CIL, "final"),
  authority(F.TRUEUP, "final"),
  authority(F.FSA, "final"),
  authority(F.LPS, "final"),
  authority(F.CAP, "pending_appeal"),
  authority(F.CIL_EXT, "final"),
  authority(F.ADV_WC, "pending_appeal"),
  authority(F.ADV_DGC, "final"),
  authority(F.ADV_OM, "stayed"),
  authority(F.ADV_IDP, "pending_appeal"),
  authority(F.ADV_TL, "final"),
  authority(F.ADV_CQ, "pending_appeal"),

  // The golden case
  authority(GOLDEN.tataPower, "final"),
  authority(GOLDEN.jpvlMpercDisallowed, "reversed"),
  authority(GOLDEN.aptel2024, "superseded"),
  authority(GOLDEN.aptel2025, "pending_appeal"),
];

// ---------------------------------------------------------------------------
// Company agreements + clauses (kept minimal - just enough for every
// CompanyDisallowance.clause_id below to resolve to a real record)
// ---------------------------------------------------------------------------

const A = {
  A1: aid("A1"),
  A2: aid("A2"),
  A3: aid("A3"),
  A4: aid("A4"),
  A5: aid("A5"),
} as const;

export const COMPANY_AGREEMENTS: CompanyAgreement[] = [
  { id: A.A1, name: "PPA - Suryachal TPS Unit 1&2 (1000MW)", counterparty: DISCOM_1, forum: "state_commission", agreement_type: "cost_plus", effective_date: "2011-04-01", contracted_share: null, tariff_basis: null, share_basis_note: null },
  { id: A.A2, name: "PPA - Suryachal TPS Unit 3 (660MW Expansion)", counterparty: DISCOM_1, forum: "state_commission", agreement_type: "cost_plus", effective_date: "2016-01-10", contracted_share: null, tariff_basis: null, share_basis_note: null },
  { id: A.A3, name: "PPA - Konkan Coastal Unit 2 (660MW)", counterparty: DISCOM_3, forum: "central_tribunal", agreement_type: "bid_route", effective_date: "2014-08-15", contracted_share: null, tariff_basis: null, share_basis_note: null },
  { id: A.A4, name: "PPA - Godavari Peaking Station (350MW)", counterparty: DISCOM_2, forum: "appellate_tribunal", agreement_type: "bid_route", effective_date: "2013-03-05", contracted_share: null, tariff_basis: null, share_basis_note: null },
  { id: A.A5, name: "Fuel Supply Agreement - Konkan Coastal Linkage", counterparty: COAL_CO, forum: "central_ministry", agreement_type: "fuel_supply", effective_date: "2009-06-20", contracted_share: null, tariff_basis: null, share_basis_note: null },

  // The golden case (real)
  {
    id: GOLDEN.agreement,
    name: "Regulated MP PPA (30%) - MB Power (MP) Ltd / MP Tradeco (now MPPMCL)",
    counterparty: "M.P. Power Management Company Ltd (formerly M.P. Power Trading Co. Ltd)",
    forum: "state_commission",
    agreement_type: "cost_plus",
    effective_date: "2011-01-05",
    contracted_share: 0.3,
    tariff_basis: "full_tariff",
    share_basis_note: "PPA dated 05.01.2011: Contracted Capacity = 30% of installed capacity.",
  },
];

interface ClauseSeed {
  key: string;
  agreementId: string;
  clauseRef: string;
  category: string;
  text: string;
}

const CLAUSE_SEEDS: ClauseSeed[] = [
  { key: "CL1", agreementId: A.A2, clauseRef: "Article 9.2", category: "working_capital", text: "Normative working capital shall be computed per Regulation 34 and shall be deemed to fully compensate the Seller's receivables carrying cost absent a demonstrated deviation." },
  { key: "CL2", agreementId: A.A1, clauseRef: "Article 11.4", category: "deemed_generation", text: "Deemed generation compensation is payable only where the backing-down instruction is independently traceable to a grid-security constraint and not to a Seller-side protection event." },
  { key: "CL3", agreementId: A.A3, clauseRef: "Article 7.6", category: "o_and_m_escalation", text: "The O&M escalation formula shall track the WPI-linked index for the base Contract Year, with any subsequent index change subject to Commission approval." },
  { key: "CL4", agreementId: A.A4, clauseRef: "Article 12.1", category: "late_payment_interest", text: "Interest on delayed payments beyond the surcharge already payable under Article 12.4 may be claimed only on a showing of separate, non-duplicative loss." },
  { key: "CL5", agreementId: A.A1, clauseRef: "Article 8.3", category: "transmission_loss", text: "Transmission losses shall be shared per the applicable Grid Code formula unless this Agreement specifically provides otherwise." },
  { key: "CL6", agreementId: A.A5, clauseRef: "Clause 11.2", category: "coal_quality", text: "Compensation for grade slippage shall be certified quarterly by an independent sampling agency nominated jointly by the Parties." },
  { key: "CL9", agreementId: A.A1, clauseRef: "Article 10.3(a)", category: "change_in_law", text: "Any new tax, cess, duty or levy imposed by the Central or a State Government after the Effective Date, of general application, constitutes a Change in Law entitling the Seller to full pass-through, continuing for as long as the levy remains in force." },
  { key: "CL10", agreementId: A.A3, clauseRef: "Article 14.1", category: "tariff_true_up", text: "Either Party may apply for a true-up of the tariff for a Control Period within two (2) years of the date on which the relevant tariff order is communicated to that Party." },
  { key: "CL11", agreementId: A.A5, clauseRef: "Clause 9.4", category: "fuel_cost_pass_through", text: "Where the actual quantity of coal supplied under the annual linkage falls short of the Normative Requirement for reasons not attributable to the Seller, the Fuel Supplier shall compensate the Seller for the certified shortfall." },
  { key: "CL12", agreementId: A.A4, clauseRef: "Article 12.4", category: "late_payment_surcharge", text: "A late payment surcharge accrues automatically on any amount unpaid beyond the due date, at the rate specified in the applicable tariff regulations." },
  { key: "CL13", agreementId: A.A2, clauseRef: "Article 6.4", category: "capacity_charge", text: "Capacity Charges shall be reduced only to the extent of Forced Outage Hours independently verified against the Seller's daily generation log and the Grid Operator's dispatch instructions." },
];

const CL = Object.fromEntries(CLAUSE_SEEDS.map((s) => [s.key, clid(s.key)])) as Record<string, string>;

export const AGREEMENT_CLAUSES: AgreementClause[] = [
  ...CLAUSE_SEEDS.map((seed) => ({
    id: CL[seed.key],
    agreement_id: seed.agreementId,
    clause_ref: seed.clauseRef,
    text: seed.text,
    category: seed.category,
    has_source_document: false,
  })),

  // Golden case (real PPA clauses; RoE entitlement comes from MPERC regulations, not a contract clause).
  {
    id: GOLDEN.clauseTariff,
    agreement_id: GOLDEN.agreement,
    clause_ref: "Article 10.1.1",
    category: "tariff_determination",
    text: "The Tariff shall comprise Capacity Charge, Variable Charge and any other charges as may be determined by the Appropriate Commission under Law.",
    has_source_document: false,
  },
  {
    id: GOLDEN.clauseChangeInLaw,
    agreement_id: GOLDEN.agreement,
    clause_ref: "Article 12.1.1(ii)",
    category: "change_in_law",
    text: "Change in Law includes a change in the interpretation of any Law by any Competent Court of Law.",
    has_source_document: false,
  },
  {
    id: GOLDEN.clauseCarryingCost,
    agreement_id: GOLDEN.agreement,
    clause_ref: "Article 10.4.2",
    category: "carrying_cost",
    text: "Late Payment Surcharge at 1.25% per month, compounded on monthly rests.",
    has_source_document: false,
  },
];

// ---------------------------------------------------------------------------
// Company disallowance history
// ---------------------------------------------------------------------------

interface DisallowanceSeed {
  key: string;
  clauseKey: string;
  petitionRef: string;
  amountClaimed: number;
  amountAllowed: number;
  reason: string;
  forum: Forum;
  status: string;
  filedDate: string;
}

const DISALLOWANCE_SEEDS: DisallowanceSeed[] = [
  { key: "D1", clauseKey: "CL1", petitionRef: "Petition No. 97 of 2023", amountClaimed: 15200000, amountAllowed: 0, reason: "Working capital carrying cost claim rejected under normative O&M norms", forum: "state_commission", status: "under_review", filedDate: "2023-05-10" },
  { key: "D2", clauseKey: "CL2", petitionRef: "Civil Appeal No. 3007 of 2021", amountClaimed: 22500000, amountAllowed: 0, reason: "Deemed generation compensation claim rejected; outage held generator-attributable", forum: "supreme_court", status: "dismissed", filedDate: "2020-11-18" },
  { key: "D3", clauseKey: "CL3", petitionRef: "Appeal No. 203 of 2024", amountClaimed: 41000000, amountAllowed: 12000000, reason: "CPI-linked escalation component stayed pending a larger bench reference", forum: "appellate_tribunal", status: "under_review", filedDate: "2024-02-22" },
  { key: "D4", clauseKey: "CL4", petitionRef: "Petition No. 212 of 2024", amountClaimed: 8600000, amountAllowed: 0, reason: "Interest on delayed payments held not maintainable alongside the surcharge clause", forum: "state_commission", status: "under_review", filedDate: "2024-08-30" },
  { key: "D5", clauseKey: "CL5", petitionRef: "Petition No. 88/MP/2023", amountClaimed: 6400000, amountAllowed: 0, reason: "Transmission loss sharing claim rejected; grid code formula held to prevail", forum: "central_tribunal", status: "dismissed", filedDate: "2023-02-14" },
  { key: "D6", clauseKey: "CL6", petitionRef: "Petition No. 145 of 2025", amountClaimed: 17300000, amountAllowed: 4100000, reason: "Grade slippage compensation allowed for 2 of 4 quarters pending an independent sampling report", forum: "state_commission", status: "under_review", filedDate: "2025-04-02" },
  { key: "D7", clauseKey: "CL1", petitionRef: "Petition No. 233 of 2025", amountClaimed: 9200000, amountAllowed: 2000000, reason: "Interim order allows partial working capital carrying cost pending final hearing", forum: "state_commission", status: "under_review", filedDate: "2025-11-12" },
  { key: "D8", clauseKey: "CL3", petitionRef: "Petition No. 301 of 2025", amountClaimed: 5400000, amountAllowed: 0, reason: "Escalation rate dispute for FY2024-25 held over for a combined hearing with Appeal No. 203 of 2024", forum: "appellate_tribunal", status: "filed", filedDate: "2026-03-02" },
  { key: "D9", clauseKey: "CL9", petitionRef: "Petition No. 145 of 2022", amountClaimed: 68000000, amountAllowed: 41000000, reason: "Coal cess pass-through for FY2021-23 allowed only up to the then-affirmed computation basis; balance held over pending Civil Appeal No. 4521 of 2021", forum: "state_commission", status: "disposed", filedDate: "2022-04-11" },
  { key: "D10", clauseKey: "CL10", petitionRef: "Petition No. 58 of 2021", amountClaimed: 71000000, amountAllowed: 0, reason: "True-up claim for FY2019-21 held in abeyance pending the Supreme Court's decision on limitation in Civil Appeal No. 6210 of 2020", forum: "state_commission", status: "disposed", filedDate: "2021-03-09" },
  { key: "D11", clauseKey: "CL11", petitionRef: "OM No. F.10-22/2024-Power", amountClaimed: 19500000, amountAllowed: 0, reason: "FY2024-25 shortfall compensation deferred pending nodal agency certification", forum: "central_ministry", status: "disposed", filedDate: "2024-06-01" },
  { key: "D12", clauseKey: "CL12", petitionRef: "Petition No. 40 of 2023", amountClaimed: 11200000, amountAllowed: 0, reason: "Late payment surcharge for FY2022-23 held over pending the Tribunal's ruling in Appeal No. 118 of 2024", forum: "state_commission", status: "disposed", filedDate: "2023-05-20" },
  { key: "D13", clauseKey: "CL13", petitionRef: "Petition No. 55 of 2022", amountClaimed: 24000000, amountAllowed: 0, reason: "Capacity charge for forced-outage hours for FY2021-22 disallowed for want of independently verified logs", forum: "state_commission", status: "disposed", filedDate: "2022-07-14" },
];

const D = Object.fromEntries(DISALLOWANCE_SEEDS.map((s) => [s.key, did(s.key)])) as Record<string, string>;

export const COMPANY_DISALLOWANCES: CompanyDisallowance[] = [
  ...DISALLOWANCE_SEEDS.map((seed) => ({
    id: D[seed.key],
    clause_id: CL[seed.clauseKey],
    petition_ref: seed.petitionRef,
    amount_claimed: seed.amountClaimed,
    amount_allowed: seed.amountAllowed,
    disallowance_reason: seed.reason,
    forum: seed.forum,
    status: seed.status,
    filed_date: seed.filedDate,
  })),
];

// ---------------------------------------------------------------------------
// Claim candidates - populate every pipeline stage with created_at dates
// spread across the last three months for the periodic digest
// ---------------------------------------------------------------------------

function claimOntologyScores(confidence: number, limitationFlag: boolean) {
  return {
    score_applicability: confidence,
    score_legal_stability: Math.min(1, confidence * 0.95 + 0.02),
    score_recoverability: Math.min(1, confidence * 0.9),
    score_limitation: limitationFlag ? Math.max(0.15, (1 - confidence) * 0.55) : Math.min(0.95, confidence + 0.05),
  };
}

interface ClaimCandidateSeed {
  key: string;
  findingId: string;
  disallowanceKey: string | null;
  clauseKey: string | null;
  issueTag: string;
  precedentChain: string[];
  valueLow: number;
  valueHigh: number;
  valueBasis: string;
  recommendedForum: string;
  limitationFlag: boolean;
  limitationNote: string | null;
  confidence: number;
  status: ClaimCandidateStatus;
  rejectionReason: string | null;
  draftArgument: string | null;
  createdAt: string;
}

const CLAIM_CANDIDATE_SEEDS: ClaimCandidateSeed[] = [
  { key: "CC1", findingId: F.CIL, disallowanceKey: "D9", clauseKey: "CL9", issueTag: "change_in_law_coal_cess", precedentChain: [F.CIL_EXT, F.CIL], valueLow: 24000000, valueHigh: 27000000, valueBasis: "Balance coal cess pass-through for FY2021-23 held over pending the Supreme Court's ruling in Civil Appeal No. 4521 of 2021, now unlocked in full.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.93, status: "recovered", rejectionReason: null, draftArgument: null, createdAt: "2026-02-10T09:00:00Z" },
  { key: "CC2", findingId: F.TRUEUP, disallowanceKey: "D10", clauseKey: "CL10", issueTag: "tariff_true_up_denial", precedentChain: [F.TRUEUP], valueLow: 62000000, valueHigh: 71000000, valueBasis: "FY2019-21 true-up principal held in abeyance, now payable in full following the Supreme Court's affirmance of the communication-of-order limitation rule.", recommendedForum: "appellate_tribunal", limitationFlag: true, limitationNote: "Recovery petition should be filed within 2 years of the Supreme Court order dated 2024-10-09; window closes 2026-10-09.", confidence: 0.88, status: "filed", rejectionReason: null, draftArgument: null, createdAt: "2026-03-18T10:30:00Z" },
  { key: "CC3", findingId: F.FSA, disallowanceKey: "D11", clauseKey: "CL11", issueTag: "fuel_supply_agreement_shortfall", precedentChain: [F.FSA], valueLow: 17000000, valueHigh: 19500000, valueBasis: "FY2024-25 certified linkage shortfall compensation, deferred pending nodal agency certification, now payable on the same computation basis as OM No. F.10-18/2024-Power.", recommendedForum: "central_ministry", limitationFlag: false, limitationNote: null, confidence: 0.85, status: "allowed", rejectionReason: null, draftArgument: null, createdAt: "2026-04-02T08:15:00Z" },
  { key: "CC4", findingId: F.LPS, disallowanceKey: "D12", clauseKey: "CL12", issueTag: "late_payment_surcharge", precedentChain: [F.LPS], valueLow: 9500000, valueHigh: 11200000, valueBasis: "FY2022-23 late payment surcharge, held over pending the Tribunal's ruling, now recoverable per Appeal No. 118 of 2024.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.9, status: "allowed", rejectionReason: null, draftArgument: null, createdAt: "2026-04-20T11:00:00Z" },
  { key: "CC5", findingId: F.CAP, disallowanceKey: "D13", clauseKey: "CL13", issueTag: "capacity_charge_forced_outage", precedentChain: [F.CAP], valueLow: 18000000, valueHigh: 24000000, valueBasis: "FY2021-22 capacity charge for forced-outage hours, disallowed for want of verified logs, now addressed by the Tribunal's recomputation in Appeal No. 96 of 2023.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.68, status: "validated", rejectionReason: null, draftArgument: null, createdAt: "2026-05-05T09:45:00Z" },
  { key: "CC6", findingId: F.CIL, disallowanceKey: null, clauseKey: null, issueTag: "change_in_law_coal_cess", precedentChain: [F.CIL], valueLow: 6000000, valueHigh: 9000000, valueBasis: "Unit-2 proportionate coal cess differential for FY2021-23, not covered by the Petition No. 145 of 2022 order and newly identified as a distinct billing head.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.72, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-08-05T09:00:00Z" },
  { key: "CC7", findingId: F.TRUEUP, disallowanceKey: null, clauseKey: "CL10", issueTag: "tariff_true_up_denial", precedentChain: [F.TRUEUP], valueLow: 8000000, valueHigh: 12000000, valueBasis: "Interest component on the FY2019-21 true-up principal under Article 14.3, not yet quantified in the original petition.", recommendedForum: "appellate_tribunal", limitationFlag: true, limitationNote: "Should be filed alongside the principal recovery petition before the 2026-10-09 window closes.", confidence: 0.66, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-08-14T13:20:00Z" },
  { key: "CC8", findingId: F.FSA, disallowanceKey: null, clauseKey: null, issueTag: "fuel_supply_agreement_shortfall", precedentChain: [F.FSA], valueLow: 4500000, valueHigh: 6500000, valueBasis: "FY2025-26 Q1 shortfall newly certified by the nodal agency; not yet the subject of any petition.", recommendedForum: "central_ministry", limitationFlag: false, limitationNote: null, confidence: 0.7, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-09-02T09:10:00Z" },
  { key: "CC9", findingId: F.LPS, disallowanceKey: null, clauseKey: "CL12", issueTag: "late_payment_surcharge", precedentChain: [F.LPS], valueLow: 3000000, valueHigh: 4800000, valueBasis: "FY2023-24 surcharge on a second, smaller billing cycle not covered by Petition No. 40 of 2023.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.75, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-09-05T10:00:00Z" },
  { key: "CC10", findingId: F.CAP, disallowanceKey: null, clauseKey: null, issueTag: "capacity_charge_forced_outage", precedentChain: [F.CAP], valueLow: 5000000, valueHigh: 8000000, valueBasis: "FY2022-23 forced-outage hours at Unit 3, awaiting the same recomputation formula once the pending Supreme Court SLP is resolved.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.55, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-09-09T14:00:00Z" },
  { key: "CC11", findingId: F.CIL, disallowanceKey: null, clauseKey: "CL9", issueTag: "change_in_law_coal_cess", precedentChain: [F.CIL], valueLow: 40000000, valueHigh: 55000000, valueBasis: "Coal cess differential across both Suryachal TPS units for FY2024-26, computed prospectively on the Supreme Court-affirmed basis.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.8, status: "validated", rejectionReason: null, draftArgument: null, createdAt: "2026-07-22T08:30:00Z" },
  { key: "CC12", findingId: F.TRUEUP, disallowanceKey: null, clauseKey: "CL10", issueTag: "tariff_true_up_denial", precedentChain: [F.TRUEUP], valueLow: 25000000, valueHigh: 32000000, valueBasis: "FY2021-23 true-up principal for Konkan Coastal Unit 2, structured identically to the Supreme Court-affirmed FY2019-21 claim.", recommendedForum: "appellate_tribunal", limitationFlag: false, limitationNote: null, confidence: 0.82, status: "validated", rejectionReason: null, draftArgument: null, createdAt: "2026-07-30T09:00:00Z" },
  { key: "CC13", findingId: F.FSA, disallowanceKey: null, clauseKey: "CL11", issueTag: "fuel_supply_agreement_shortfall", precedentChain: [F.FSA], valueLow: 10000000, valueHigh: 14000000, valueBasis: "FY2024-25 full-year shortfall compensation on the affirmed computation basis.", recommendedForum: "central_ministry", limitationFlag: false, limitationNote: null, confidence: 0.84, status: "validated", rejectionReason: null, draftArgument: null, createdAt: "2026-08-11T09:30:00Z" },
  { key: "CC14", findingId: F.LPS, disallowanceKey: null, clauseKey: "CL12", issueTag: "late_payment_surcharge", precedentChain: [F.LPS], valueLow: 6500000, valueHigh: 9000000, valueBasis: "FY2024-25 surcharge across three delayed billing cycles at Godavari Peaking Station.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.79, status: "validated", rejectionReason: null, draftArgument: null, createdAt: "2026-08-19T10:15:00Z" },
  { key: "CC15", findingId: F.CIL, disallowanceKey: null, clauseKey: "CL9", issueTag: "change_in_law_coal_cess", precedentChain: [F.CIL], valueLow: 30000000, valueHigh: 38000000, valueBasis: "Coal cess differential at Konkan Coastal Unit 1, filed as a follow-on petition citing the Supreme Court's ruling directly.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.87, status: "filed", rejectionReason: null, draftArgument: null, createdAt: "2026-06-12T09:00:00Z" },
  { key: "CC16", findingId: F.TRUEUP, disallowanceKey: null, clauseKey: "CL10", issueTag: "tariff_true_up_denial", precedentChain: [F.TRUEUP], valueLow: 14000000, valueHigh: 18000000, valueBasis: "FY2020-21 companion true-up petition, previously dismissed as infructuous, refiled on the affirmed limitation basis.", recommendedForum: "state_commission", limitationFlag: true, limitationNote: "Refiling deadline under the Tribunal's practice direction falls on 2026-11-30.", confidence: 0.7, status: "filed", rejectionReason: null, draftArgument: null, createdAt: "2026-06-25T09:00:00Z" },
  { key: "CC17", findingId: F.LPS, disallowanceKey: null, clauseKey: "CL12", issueTag: "late_payment_surcharge", precedentChain: [F.LPS], valueLow: 5000000, valueHigh: 7000000, valueBasis: "Proposed surcharge claim for a third billing cycle at Godavari Peaking Station.", recommendedForum: "state_commission", limitationFlag: true, limitationNote: "Limitation period for this billing cycle expired 2025-03-01, before the unlocking Tribunal decision.", confidence: 0.4, status: "rejected", rejectionReason: "Limitation lapsed before the Tribunal's ruling in Appeal No. 118 of 2024; no condonation ground identified.", draftArgument: null, createdAt: "2026-05-14T09:00:00Z" },
  { key: "CC18", findingId: F.CAP, disallowanceKey: "D13", clauseKey: "CL13", issueTag: "capacity_charge_forced_outage", precedentChain: [F.CAP], valueLow: 9000000, valueHigh: 13000000, valueBasis: "FY2023-24 forced-outage hours at Unit 3, pending finalisation of the generation-log reconciliation before the same computation is applied.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.6, status: "deferred", rejectionReason: null, draftArgument: null, createdAt: "2026-07-08T09:00:00Z" },
  { key: "CC19", findingId: F.FSA, disallowanceKey: null, clauseKey: "CL11", issueTag: "fuel_supply_agreement_shortfall", precedentChain: [F.FSA], valueLow: 8000000, valueHigh: 10500000, valueBasis: "FY2023-24 shortfall balance at Konkan Coastal Unit 1, filed as a supplementary claim citing OM No. F.10-18/2024-Power.", recommendedForum: "central_ministry", limitationFlag: false, limitationNote: null, confidence: 0.81, status: "filed", rejectionReason: null, draftArgument: null, createdAt: "2026-06-30T09:00:00Z" },
  { key: "CC20", findingId: F.TRUEUP, disallowanceKey: null, clauseKey: "CL10", issueTag: "tariff_true_up_denial", precedentChain: [F.TRUEUP], valueLow: 20000000, valueHigh: 24000000, valueBasis: "FY2018-19 true-up shortfall for Konkan Coastal Unit 2, allowed on the affirmed limitation basis.", recommendedForum: "appellate_tribunal", limitationFlag: false, limitationNote: null, confidence: 0.89, status: "allowed", rejectionReason: null, draftArgument: null, createdAt: "2026-03-05T09:00:00Z" },
  { key: "CC21", findingId: F.LPS, disallowanceKey: null, clauseKey: "CL12", issueTag: "late_payment_surcharge", precedentChain: [F.LPS], valueLow: 4200000, valueHigh: 4200000, valueBasis: "FY2021-22 surcharge arrears at Godavari Peaking Station, recovered in full following the Tribunal's ruling.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.95, status: "recovered", rejectionReason: null, draftArgument: null, createdAt: "2026-01-20T09:00:00Z" },
  { key: "CC22", findingId: F.CIL, disallowanceKey: null, clauseKey: null, issueTag: "change_in_law_coal_cess", precedentChain: [F.CIL], valueLow: 12000000, valueHigh: 16000000, valueBasis: "Coal cess differential at the Godavari Peaking Station gas plant, newly flagged as potentially analogous pending confirmation the cess applied to gas-fired stations.", recommendedForum: "state_commission", limitationFlag: false, limitationNote: null, confidence: 0.5, status: "identified", rejectionReason: null, draftArgument: null, createdAt: "2026-09-10T09:00:00Z" },
];

export const CLAIM_CANDIDATES: ClaimCandidate[] = [
  ...CLAIM_CANDIDATE_SEEDS.map((seed) => ({
  id: ccid(seed.key),
  finding_id: seed.findingId,
  disallowance_id: seed.disallowanceKey ? D[seed.disallowanceKey] : null,
  issue_tag: seed.issueTag,
  precedent_chain: seed.precedentChain,
  matched_clause_id: seed.clauseKey ? CL[seed.clauseKey] : null,
  reopening_clause_id: null,
  entitlement_gap_id: null,
  value_breakdown: null,
  filing_deadline: null,
  filing_deadline_note: null,
  value_low: seed.valueLow,
  value_high: seed.valueHigh,
  value_basis: seed.valueBasis,
  recommended_forum: seed.recommendedForum,
  limitation_flag: seed.limitationFlag,
  limitation_note: seed.limitationNote,
  confidence_score: seed.confidence,
  confidence_breakdown: null,
  counsel_validated_at: seed.status === "recovered" ? seed.createdAt : null,
  ...claimOntologyScores(seed.confidence, seed.limitationFlag),
  status: seed.status,
  rejection_reason: seed.rejectionReason,
  draft_argument: seed.draftArgument,
  draft_argument_original: seed.draftArgument,
  created_at: seed.createdAt,
  jurisdiction_binding: null,
  jurisdiction_note: null,
  financial_year: null,
  fact_pattern_id: null,
  })),

  // Golden case (real): one FY priced from an entitlement gap — MB Power never
  // filed a disallowance petition for RoE gross-up; the gap was unclaimed.
  {
    id: GOLDEN.claimCandidate,
    finding_id: GOLDEN.aptel2024,
    disallowance_id: null,
    issue_tag: "roe_tax_gross_up",
    precedent_chain: [GOLDEN.tataPower],
    matched_clause_id: GOLDEN.clauseTariff,
    reopening_clause_id: GOLDEN.clauseChangeInLaw,
    entitlement_gap_id: GOLDEN.gapFy201516,
    value_breakdown: {
      principal_low: 96000000,
      principal_high: 125600000,
      carrying_cost_low: 14000000,
      carrying_cost_high: 18000000,
      carrying_cost_rate_pct_per_month: 1.25,
      carrying_cost_compounding: "monthly",
      carrying_cost_from: "2016-03-31",
      share_applied: 0.3,
    },
    filing_deadline: "2024-11-15",
    filing_deadline_note: "Next true-up filing under MPERC Regulations 2024, Regulation 9.7.",
    value_low: 110000000,
    value_high: 143600000,
    value_basis: "FY2015-16 RoE gross-up gap at 30% contracted share, per Regulation 22.3 and Table 31 of MPERC Order 29.05.2026.",
    recommended_forum: "state_commission",
    limitation_flag: false,
    limitation_note: "Change in Law under Article 12.1.1(ii); cause of action from APTEL 22.03.2024.",
    confidence_score: 0.91,
    confidence_breakdown: null,
    counsel_validated_at: null,
    score_applicability: 0.91,
    score_legal_stability: 0.88,
    score_recoverability: 0.85,
    score_limitation: 0.82,
    status: "allowed",
    rejection_reason: null,
    draft_argument:
      "Entitlement gap: under Regulation 22.3, MB Power was entitled to gross up RoE for FY2015-16 but never claimed it in any petition. APTEL 22.03.2024 makes the gap claimable via Change in Law.",
    draft_argument_original:
      "Entitlement gap: under Regulation 22.3, MB Power was entitled to gross up RoE for FY2015-16 but never claimed it in any petition. APTEL 22.03.2024 makes the gap claimable via Change in Law.",
    created_at: "2024-03-23T09:00:00Z",
    jurisdiction_binding: "binding",
    jurisdiction_note: null,
    financial_year: "FY2015-16",
    fact_pattern_id: null,
  },
];

// ---------------------------------------------------------------------------
// Change-in-Law changed events (gold CE1 and CE3, docs/01-corpus-analysis.md §9.2)
// ---------------------------------------------------------------------------


const CERC_COAL_SIZING: CilDecision = {
  id: "mock-dec-cerc-c",
  event_key: "coal_sizing_surface_transport",
  event_label: "Coal sizing and surface transportation charges",
  event_phrase: "Change in Coal Sizing Charges and Surface Transportation Cost by Coal India Ltd.",
  party_name: "M. B. Power (Madhya Pradesh) Limited",
  is_hp: true,
  forum_name: "CERC",
  case_ref: "CERC/MP/156/2018",
  order_date: "2019-06-03",
  period_key: "from:2019-06-03",
  polarity: "disallowed",
  effective_polarity: "disallowed",
  authority_status: "final",
  amount_claimed_cr: null,
  amount_allowed_cr: null,
  quote: "c Change in Coal Sizing Charges and Surface Not Allowed",
  page: 75,
  source_path: "rules",
  confidence: 0.95,
  source_document_id: "mock-doc-cerc",
  file_name: "MB Power Order dated 03.06.2019 in 156-MP-2018.pdf",
  has_source_document: false,
  review_state: "accepted",
};

const RATTAN_APTEL: CilDecision = {
  ...CERC_COAL_SIZING,
  id: "mock-dec-rattan",
  event_phrase: "sizing/ crushing charges and surface transportation cost of coal",
  party_name: "RattanIndia Power Limited",
  is_hp: false,
  forum_name: "APTEL",
  case_ref: "APTEL/APL/345/2021",
  order_date: "2025-05-08",
  period_key: "from:2025-05-08",
  polarity: "set_aside",
  effective_polarity: "allowed",
  authority_status: "pending_appeal",
  quote: "the impugned order of the Commission is set aside",
  page: 73,
  source_path: "llm",
  confidence: 0.9,
  source_document_id: "mock-doc-rattan",
  file_name: "Rattan India vs MERC_Order dated 08.05.2025.pdf",
};

const MB_FY2425_ROE: CilDecision = {
  ...CERC_COAL_SIZING,
  id: "mock-dec-mb-fy2425",
  event_key: "roe_mat_grossup",
  event_label: "Grossing up of RoE with MAT",
  event_phrase: "grossing up the base rate",
  forum_name: "MPERC",
  case_ref: "MPERC/P/4/2026",
  order_date: "2026-05-29",
  period_key: "FY2015-16..FY2024-25",
  polarity: "allowed",
  effective_polarity: "allowed",
  quote: "the Commission hereby allows and approves the Grossing-up of the base rate of RoE",
  page: 59,
  source_path: "rules",
  file_name: "12. True-up Order FY 2024-25.pdf",
};

const SC_STAY: CilDecision = {
  ...CERC_COAL_SIZING,
  id: "mock-dec-sc-stay",
  event_key: null,
  event_label: null,
  event_phrase: "(status action on the order under appeal)",
  party_name: null,
  is_hp: false,
  forum_name: "SC",
  case_ref: "SC/DIARY/67192/2025",
  order_date: "2026-02-13",
  period_key: "from:2026-02-13",
  polarity: "stayed",
  effective_polarity: null,
  quote: "However, the same shall not be given effect to pending further orders.",
  page: 2,
  file_name: "3. Order Dated 13-Feb-2026.pdf",
};

export const CIL_CHANGE_EVENTS: CilChangeEvent[] = [
  {
    id: "mock-ce1",
    direction: "disallowed_to_allowed",
    trigger_scope: "peer",
    trigger_kind: "reversal",
    strength: "provisional",
    status: "new",
    counts_on_card: true,
    event_key: "coal_sizing_surface_transport",
    event_label: "Coal sizing and surface transportation charges",
    jurisdiction: "CENTRAL",
    period_key: "from:2019-06-03",
    binding: "binding",
    relevance_note:
      "Same event (coal_sizing_surface_transport): HP was disallowed (CERC/MP/156/2018), now allowed in a ruling for another party (APTEL/APL/345/2021); appellate_tribunal hears appeals from central_tribunal, so its ruling binds it. Trigger is pending appeal, so this may still change.",
    baseline: CERC_COAL_SIZING,
    trigger: RATTAN_APTEL,
    supporting_ruling: null,
    impact: {
      change: "Refused → Allowed",
      effect: "HP was refused by CERC; APTEL has now allowed RattanIndia Power Limited's claim for the same cost. It binds CERC. HP can claim through Case-1 UP PPA, Art. 10.",
      next_step: "Confirm to open a claim; the ruling is still under appeal, so it may change.",
    },
    route: "Case-1 UP PPA, Art. 10",
    claim_candidate_id: null,
    first_detected_at: "2026-09-25T10:00:00Z",
    updated_at: "2026-09-25T10:00:00Z",
  },
  {
    id: "mock-ce3",
    direction: "allowed_to_disallowed",
    trigger_scope: "appellate_status",
    trigger_kind: "stay",
    strength: "provisional",
    status: "new",
    counts_on_card: true,
    event_key: "roe_mat_grossup",
    event_label: "Grossing up of RoE with MAT",
    jurisdiction: "MP",
    period_key: "FY2015-16..FY2024-25",
    binding: "binding",
    relevance_note:
      "HP's allowance on roe_mat_grossup (order of 29 May 2026) was granted relying on a ruling that had already been stayed on 13 Feb 2026 (supreme_court, SC/DIARY/67192/2025); the allowance cannot be given effect while that stay stands; supreme_court hears appeals from state_commission, so its ruling binds it.",
    baseline: MB_FY2425_ROE,
    trigger: SC_STAY,
    supporting_ruling: { id: "mock-ruling-aptel-113", forum_name: "APTEL", case_ref: "APTEL/APL/113/2022", order_date: null, is_placeholder: true },
    impact: {
      change: "Allowed → On hold",
      effect: "HP's allowance from MPERC rests on APTEL APTEL/APL/113/2022, which the Supreme Court put on hold before HP's order. HP cannot recover the amount until the Supreme Court decides the appeal.",
      next_step: "Treat the allowed amount as contingent; track the hearings in the same appeal.",
    },
    route: null,
    claim_candidate_id: null,
    first_detected_at: "2026-09-25T10:05:00Z",
    updated_at: "2026-09-25T10:05:00Z",
  },
];

const APTEL_JPVL_2025: CilDecision = {
  ...MB_FY2425_ROE,
  id: "mock-dec-aptel-113",
  party_name: "Jaiprakash Power Ventures Limited",
  is_hp: false,
  forum_name: "APTEL",
  case_ref: "APTEL/APL/113/2022",
  order_date: "2025-07-28",
  period_key: "FY2019-20",
  quote: "Reaffirming the 22.03.2024 judgment: grossing up of RoE with the applicable MAT rate under Regulation 22 is allowed.",
  page: 1,
  source_path: "legacy",
  file_name: "1. JPVL order dated 28.07.2025 in APL 113 of 2022.pdf",
};

export const CIL_EXPLANATIONS: Record<string, CilExplanation> = {
  "mock-ce3": {
    event_id: "mock-ce3",
    status_label: "Allowed on paper, on hold",
    status_tone: "warning",
    headline:
      "HP was allowed Grossing up of RoE with MAT for FY2015-16 to FY2024-25 by MPERC Petition 4/2026 of 29 May 2026. That allowance rests on APTEL Appeal 113/2022 of 28 Jul 2025, which Supreme Court Diary No. 67192/2025 of 13 Feb 2026 put on hold before HP's order was passed.",
    what_changed:
      "Nothing changed after HP was allowed: the precedent was already frozen when the Commission granted HP's claim. The allowance was conditional from the day it was granted - it stands on paper, but it cannot be given effect (HP cannot recover the money) until the Supreme Court decides the appeal. The change is from a clean allowance to an allowance that may be lost, which is why it sits under 'Changed from Allowed'.",
    timeline: [
      { date: "2025-07-28", role: "precedent", title: "APTEL allowed Jaiprakash Power Ventures Limited's claim for Grossing up of RoE with MAT for FY2019-20", detail: "The ruling HP's allowance relies on (HP's order cites it).", relative_to_hp: "before", caution: "Taken from an earlier summary of this order, not a verbatim quote: check it against the source.", decision: APTEL_JPVL_2025 },
      { date: "2026-02-13", role: "trigger", title: "Supreme Court put APTEL Appeal 113/2022 of 28 Jul 2025 on hold: it is not to be given effect", detail: "The ruling that raised this changed event.", relative_to_hp: "before", caution: null, decision: SC_STAY },
      { date: "2026-05-29", role: "hp_position", title: "MPERC allowed HP's claim for Grossing up of RoE with MAT for FY2015-16 to FY2024-25", detail: "The HP decision this changed event is about.", relative_to_hp: null, caution: null, decision: MB_FY2425_ROE },
    ],
    checks: [
      { label: "Same cost", verdict: "pass", explanation: "Both HP's order and APTEL Appeal 113/2022 of 28 Jul 2025 decide Grossing up of RoE with MAT." },
      { label: "HP's allowance relies on it", verdict: "pass", explanation: "MPERC Petition 4/2026 of 29 May 2026 cites APTEL Appeal 113/2022 of 28 Jul 2025 when allowing Grossing up of RoE with MAT." },
      { label: "That ruling is on hold", verdict: "warn", explanation: "Supreme Court Diary No. 67192/2025 of 13 Feb 2026: “However, the same shall not be given effect to pending further orders.” (page 2)." },
      { label: "Order of events", verdict: "info", explanation: "The stay (13 Feb 2026) came 105 days before HP's order (29 May 2026). The Commission granted HP's claim while the precedent was already frozen, so the allowance carries that condition from the start." },
      { label: "Binds HP's regulator", verdict: "pass", explanation: "The Supreme Court hears appeals from the State Commission, so its ruling binds it." },
      { label: "Final or provisional", verdict: "warn", explanation: "Provisional: the appeal is still pending or the ruling is on hold, so the final outcome is not known yet." },
    ],
    scenarios: [
      { condition: "Supreme Court dismisses the appeal or lifts the stay", outcome: "APTEL Appeal 113/2022 of 28 Jul 2025 stands; HP's allowance can be given effect and the amount recovered. Upload that order and this event closes automatically (superseded)." },
      { condition: "Supreme Court sets aside APTEL Appeal 113/2022 of 28 Jul 2025", outcome: "HP's allowance for Grossing up of RoE with MAT loses its basis. Expect the buyer (MPPMCL) to seek a revision; this event becomes a firm risk." },
      { condition: "Until then", outcome: "Treat the allowed amount as contingent: do not book it as recovered, and track the hearings in the same appeal." },
    ],
    counting:
      "Included in the 'Changed Events from Allowed' number: a stay or reversal of the ruling behind an HP allowance is treated as a risk to that allowance. Here the stay already existed when HP was allowed, so the risk dates from HP's own order rather than from a later event.",
  },
  "mock-ce1": {
    event_id: "mock-ce1",
    status_label: "Refused before, winnable if the ruling survives appeal",
    status_tone: "success",
    headline:
      "HP's claim for Coal sizing and surface transportation charges was refused by CERC Petition 156/MP/2018 of 03 Jun 2019. APTEL Appeal 345/2021 of 08 May 2025 allowed RattanIndia Power Limited's claim for the same cost.",
    what_changed: "A ruling that binds HP's regulator now allows what HP was refused, so HP has grounds to claim it again (an opportunity).",
    timeline: [
      { date: "2019-06-03", role: "hp_position", title: "CERC refused HP's claim for Coal sizing and surface transportation charges", detail: "The HP decision this changed event is about.", relative_to_hp: null, caution: null, decision: CERC_COAL_SIZING },
      { date: "2025-05-08", role: "trigger", title: "APTEL set aside the earlier order: RattanIndia Power Limited's claim for Coal sizing and surface transportation charges is now allowed", detail: "The ruling that raised this changed event.", relative_to_hp: "after", caution: null, decision: RATTAN_APTEL },
    ],
    checks: [
      { label: "Same cost", verdict: "pass", explanation: "Both decisions are about Coal sizing and surface transportation charges." },
      { label: "Opposite result", verdict: "pass", explanation: "HP's claim was refused; the new ruling allowed it." },
      { label: "Newer, or from a higher forum", verdict: "pass", explanation: "The ruling (08 May 2025) came 2166 days after HP's decision (03 Jun 2019)." },
      { label: "HP can use it", verdict: "pass", explanation: "Through Case-1 UP PPA Art. 10 (change in law)." },
      { label: "Binds HP's regulator", verdict: "pass", explanation: "APTEL hears appeals from CERC, so its ruling binds it." },
      { label: "Final or provisional", verdict: "warn", explanation: "Provisional: the appeal is still pending or the ruling is on hold, so the final outcome is not known yet." },
    ],
    scenarios: [
      { condition: "You confirm it", outcome: "A claim is opened and priced with the usual scoring; counsel decides whether to file." },
      { condition: "The ruling is appealed and stayed", outcome: "The event becomes provisional and stays under watch." },
      { condition: "HP's own later order allows the cost", outcome: "HP has what the event was about; it closes as realised." },
    ],
    counting: "Included in the 'Changed Events from Disallowed' number: it binds HP's regulator and is an actual allowance.",
  },
};

export const CIL_SUMMARY: CilSummary = {
  as_of: "2026-09-25T10:05:00Z",
  disallowed_to_allowed: { count: 1, provisional: 1, watching: 0 },
  allowed_to_disallowed: { count: 1, provisional: 1, watching: 0 },
};
